import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { CircularProgress, Typography, Container, Button } from "@mui/material";
import ProductDetail from "../components/ProductDetail/ProductDetail";
import { ProductWithImages } from "../types/Product";
import { ProductVariantAttributeWithValues } from "../types/ProductVariant";
import { HttpProduct } from "../http";

const ProductDetailPage: React.FC = () => {
    const { sku } = useParams<{ sku: string }>();
    const [product, setProduct] = useState<ProductWithImages | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [variantAttributes, setVariantAttributes] = useState<ProductVariantAttributeWithValues[]>(
        [],
    );

    useEffect(() => {
        const fetchProduct = async () => {
            if (!sku) {
                setError("SKU de producto no válido");
                setLoading(false);
                return;
            }

            try {
                setLoading(true);
                setError(null);

                const productWithImages = await HttpProduct.getProductBySku(sku);
                setProduct(productWithImages);

                const productAttributes = await HttpProduct.getProductVariantAttributesWithValues(
                    productWithImages.producto.id,
                );
                setVariantAttributes(productAttributes);
            } catch (err) {
                console.error("Error fetching product:", err);
                setError("Error al cargar el producto");
                setProduct(null);
            } finally {
                setLoading(false);
            }
        };

        fetchProduct();
    }, [sku]);

    const hasProductForCurrentRoute = product?.producto.sku === sku;

    if (loading || (product && !hasProductForCurrentRoute)) {
        return (
            <Container maxWidth="lg" sx={{ py: 8, textAlign: "center" }}>
                <CircularProgress size={60} />
                <Typography variant="h6" sx={{ mt: 2 }}>
                    Cargando producto...
                </Typography>
            </Container>
        );
    }

    if (error || !product || !hasProductForCurrentRoute) {
        return (
            <Container maxWidth="lg" sx={{ py: 8, textAlign: "center" }}>
                <Typography variant="h4" color="error" gutterBottom>
                    {error || "Producto no encontrado"}
                </Typography>
                <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                    No pudimos cargar la información del producto solicitado.
                </Typography>
                <Button variant="contained" onClick={() => window.location.reload()}>
                    Reintentar
                </Button>
            </Container>
        );
    }

    return <ProductDetail productWithImages={product} variantAttributes={variantAttributes} />;
};

export default ProductDetailPage;
